import React, { useEffect, useState, useRef, useContext } from "react";
import { useHighlightLayer } from "../hooks/useHighlightLayer";
import { FeatureInfoType } from "../types/FeatureInfoType";
import { useGeoApps } from "@mapgear/geoapps-ui-framework";
import { FeatureInfoContext } from "./FeatureInfoContext";
import { LayerItemId } from "../types/LayerItemId";
import isHotkey from "is-hotkey";

export const Search = (): JSX.Element => {
    const { geoapps } = useGeoApps();
    const { goToSelected } = useHighlightLayer();
    const [searchbarOpen, setSearchbarOpen] = useState(false);
    const [layers, setLayers] = useState<any[]>([]);
    const { setFeatureInfo, featureInfo } = useContext(FeatureInfoContext);
    const [value, setValue] = useState<string>("");
    const [searchFailed, setSearchFailed] = useState<boolean>(false);
    const [selectList, setSelectList] = useState<any[]>([]);
    const [nr, setNr] = useState<string>("");
    const searchRef = useRef<HTMLInputElement>(null);
    const smartscope = (geoapps as any).GetSmartScope();
    const map = geoapps.GetMap("map");

    /**
     * Keydown event
     */
    useEffect(() => {
        window.addEventListener("keydown", onKeydown);

        if (searchbarOpen) {
            focus();
        }

        return () => {
            window.removeEventListener("keydown", onKeydown);
        }
    }, [searchbarOpen]);

    /**
     * Init layers
     */
    useEffect(() => {
        if (!map) return;

        // Get layers
        if (layers.length === 0) {
            const plotLayer = map.Layers.Items.filter(layer => layer.LayerItemId === LayerItemId.PLOTS);
            if (plotLayer.length === 0) return;
            setLayers(plotLayer[0].ChildLayers);
        }

    }, [map]);

    /**
     * Set selection list when search value changes
     */
    useEffect(() => {
        if (layers.length === 0) return;

        const searchDelay = setTimeout(() => {
            if (!value) {
                setSelectList([]);
            }
            else {
                // Build request
                const datasetId = layers[0].Layer.Dataset.DatasetId;
                const requestObject = buildRequest(value, datasetId);
                // Get data table
                smartscope.GetTable(requestObject).then(res => {
                    if (res.statusCode) {
                        setSearchFailed(true);
                        return;
                    }
                    setSearchFailed(false);
                    setSelectList(res.Data);
                })
            }
        }, 300);

        return () => clearTimeout(searchDelay);
    }, [value]);

    /**
     * Set id state from url query string param
     */
    useEffect(() => {
        if (layers.length === 0) return;
        // Get id from url query string
        const urlParams = new URLSearchParams(window.location.search);
        const id = urlParams.get("kad");
        const kt = urlParams.get("kt");
        const datasetId = layers[0].Layer.Dataset.DatasetId;

        if (id) {
            // Build request
            const requestObject = buildRequest(id, datasetId);

            // Get plot nr
            smartscope.GetTable(requestObject).then(res => {
                setNr(res.Data[0].attributes.ogc_fid);
            })
        } else if (kt) {
            const newFeatureInfo: FeatureInfoType =
            {
                nr: featureInfo.nr,
                geom: featureInfo.geom,
                from: featureInfo.from,
                until: featureInfo.until,
                toggle: featureInfo.toggle + 1,
                kt: kt,
            }

            setFeatureInfo?.(newFeatureInfo);
        }
    }, [layers]);

    /**
     * Set feature info when user clicked in search results
     */
    useEffect(() => {
        if (layers.length === 0 || !nr) return;

        layers[0].GetFeatureById(nr).then(res => {
            const featureInfo: FeatureInfoType =
            {
                nr: res.OlFeature.getProperties().nr,
                geom: res.GetGeometry().AsGeoJSON(),
                from: res.OlFeature.getProperties().van,
                until: res.OlFeature.getProperties().tot,
                toggle: 0,
                kt: res.OlFeature.getProperties().kt,
            }

            setFeatureInfo?.(featureInfo);
            goToSelected(featureInfo);
            setNr("");
        })
    }, [nr])

    // Open on cmd/Ctrl+F
    const onKeydown = (e: KeyboardEvent) => {
        if (isHotkey(["ctrl+f", "cmd+f", "/", "f3"], e)) {
            e.preventDefault();
            if (searchbarOpen && searchRef.current) {
                focus();
            } else {
                setSearchbarOpen(true);
            }
        }
        if (isHotkey("enter", e) && searchRef.current) {
            searchRef.current.blur();
        }
    };

    /**
     * Focus on search input
     */
    const focus = () => {
        if (searchRef.current) {
            searchRef.current.focus();
        }
    }

    /**
     * Returns request object by given parameters
     * @param nr Number or Id of plot
     * @param datasetId Id of the dataset to request from
     * @returns Request object
     */
    const buildRequest = (nr: string, datasetId: string) => {

        const filter = {
            operator: "like",
            attribute: "nr",
            value: `%${nr.toUpperCase()}%`
        }

        const select = [
            {
                function: "value",
                attribute: "kt"
            },
            {
                function: "value",
                attribute: "van"
            },
            {
                function: "value",
                attribute: "tot"
            },
            {
                function: "value",
                attribute: "nr"
            },
            {
                function: "value",
                attribute: "ogc_fid"
            },
        ]

        const request = {
            from: datasetId,
            filter: filter,
            select: select,
        }

        return request;
    };

    /**
    * Toggles the searchbar open and closed
    */
    const toggleSearchbar = () => {
        setSearchbarOpen(!searchbarOpen);
    };

    /**
     * Handler for the input change
     * @param e change event
     */
    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setValue(value);
    };

    /**
     * Handler for clicking on a item in the selection
     * @param e click event
     */
    const handleClick = (e: any) => {
        const plotId = e.target.value;
        setNr(plotId);
    };

    /**
     * Reset the search value and selection 
     */
    const resetSelection = () => {
        setValue("");
        setSelectList([]);
        focus();
    };

    // Clear input on Escape button
    const clearInput = (e: React.KeyboardEvent<HTMLElement>) => {
        if (e.key === "Escape") {
            if (!value && searchRef.current) {
                searchRef.current.blur();
            } else {
                resetSelection();
            }
        }
    };

    return (
        <div className={searchbarOpen ? "control-group search is-open" : "control-group search"}>
            <div className={searchbarOpen ? "search-bar is-open" : "search-bar"}>
                <div className="input-wrap">
                    <input
                        autoFocus
                        className="search-input"
                        type="text"
                        size={20}
                        autoComplete="off"
                        autoCapitalize="off"
                        placeholder="Zoek perceel : a1234"
                        role="search"
                        id="searchtext20"
                        onKeyDown={clearInput}
                        onChange={(e) => handleChange(e)}
                        ref={searchRef}
                        value={value}
                    />
                    {/* show this list of items only if search result is found */}
                    {selectList.length > 0 &&
                        <ul className="search-tooltip">
                            {selectList.map(item => {
                                return <li onClick={(e) => { handleClick(e) }} value={item.attributes.ogc_fid} className="search-tip">{item.attributes.nr}</li>;
                            })}
                        </ul>
                    }
                    {
                        searchFailed &&
                        <ul className="search-tooltip">
                            <li className="search-tip">Er is iets misgegaan met het ophalen van de gegevens</li>
                        </ul>
                    }
                    {/* show cancel button only when the input containt a value */}
                    {value &&
                        <button onClick={resetSelection} className="search-cancel" title="Wissen">
                            <i className="fa-solid fa-circle-xmark"></i>
                        </button>
                    }
                </div>
                <a className="search-button" onMouseDown={(e) => e.preventDefault()} onClick={toggleSearchbar} title="Zoek perceel : a1234">
                    <i className="fa fa-search"></i>
                </a>
                {/* show search alert content only when no results are found */}
                <div className="search-alert">Perceel niet gevonden</div>
            </div>
        </div >
    );
};
